/*
 * Dhrohar Signature (0.4.0): the large photo follows the product list; the phone strip gets a count and buttons.
 *
 * Tablet/desktop (750px and up):
 * - Every list row is a real product link: a click or tap always opens the product (never a "first tap selects"
 *   trap). Pointing at a row with a mouse or pen (after a 70ms rest, so sweeping across rows on the way to the
 *   photo does not flicker through them) or moving keyboard focus into a row shows that product in the large
 *   photo and caption. The choice stays after the pointer leaves, so the photo and its heart can be reached.
 *   Touch pointers do not select on hover: iOS treats content that changes on hover as a reason to swallow the
 *   first tap, which would make links need two taps.
 * - aria-current="true" marks the row whose product is shown; the hidden cards are visibility: hidden in CSS,
 *   so only the shown photo, caption link and heart are in the tab order and the accessibility tree.
 * - No autoplay. The crossfade is CSS only and is switched off for reduced motion.
 * Phones (below 750px): the cards are a native scroll-snap strip; previous/next scroll it by one card and the
 * "1 / 4" count follows any scroll (swipe, buttons or keyboard focus).
 * All photos are fetched at low priority once the page has loaded (or at the first hover/swipe), so a change
 * never shows an empty frame. Everything is cleaned up on disconnect (theme-editor section reloads).
 */
if (!customElements.get('dhrohar-signature')) {
  class DhroharSignature extends HTMLElement {
    constructor() {
      super();
      this.controller = null;
      this.selectedIndex = 0;
      this.stripIndex = 0;
      this.targetIndex = null;
      this.hoverTimer = null;
      this.targetTimer = null;
      this.scrollFrame = null;
      this.primeLoadListener = null;
      this.primeIdleCallback = null;
      this.primeTimeout = null;
    }

    connectedCallback() {
      this.teardown();
      this.cards = Array.from(this.querySelectorAll('[data-signature-card]'));
      this.options = Array.from(this.querySelectorAll('[data-signature-option]'));
      this.strip = this.querySelector('[data-signature-strip]');
      this.prevButton = this.querySelector('[data-signature-prev]');
      this.nextButton = this.querySelector('[data-signature-next]');
      this.countText = this.querySelector('[data-signature-count-text]');
      this.countLabel = this.querySelector('[data-signature-count-label]');
      if (this.cards.length < 2) return;

      const selected = this.cards.findIndex((card) => card.classList.contains('is-selected'));
      this.selectedIndex = selected > 0 ? selected : 0;
      this.classList.add('is-enhanced');

      this.controller = new AbortController();
      const opts = { signal: this.controller.signal };
      const passive = { signal: this.controller.signal, passive: true };

      const list = this.querySelector('.dh-sig__list');
      if (list) {
        list.addEventListener('pointerover', (event) => this.onPointerOver(event), opts);
        list.addEventListener('pointerleave', () => this.clearHover(), opts);
        list.addEventListener('focusin', (event) => this.onFocusIn(event), opts);
      }
      if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1), opts);
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1), opts);
      if (this.strip) {
        this.strip.addEventListener('scroll', () => this.onScroll(), passive);
        this.strip.addEventListener('pointerdown', () => this.onStripTouch(), passive);
        this.strip.addEventListener('touchstart', () => this.onStripTouch(), passive);
      }
      window.addEventListener('resize', () => this.onScroll(), passive);

      this.schedulePrime();
      this.updateCount(this.currentStripIndex());
    }

    disconnectedCallback() {
      this.teardown();
    }

    teardown() {
      this.cancelPrime();
      this.clearHover();
      if (this.targetTimer !== null) window.clearTimeout(this.targetTimer);
      this.targetTimer = null;
      this.targetIndex = null;
      if (this.scrollFrame !== null) window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      if (this.controller) this.controller.abort();
      this.controller = null;
    }

    /* ---------- public API (also used by the interaction tests) ---------- */

    get selected() {
      return this.selectedIndex;
    }

    select(index) {
      if (!this.cards || index < 0 || index >= this.cards.length) return;
      this.clearHover();
      this.prime();
      this.selectedIndex = index;
      this.cards.forEach((card, i) => card.classList.toggle('is-selected', i === index));
      this.options.forEach((row, i) => {
        const on = i === index;
        row.classList.toggle('is-selected', on);
        const link = row.querySelector('[data-signature-link]');
        if (!link) return;
        if (on) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
    }

    /* ---------- list: hover and focus pick the photo ---------- */

    rowIndex(target) {
      const row = target instanceof Element ? target.closest('[data-signature-option]') : null;
      if (!row || !this.contains(row)) return -1;
      return Number(row.dataset.signatureOption);
    }

    onPointerOver(event) {
      if (event.pointerType === 'touch') return;
      const index = this.rowIndex(event.target);
      if (index < 0) return;
      this.prime();
      this.clearHover();
      if (index === this.selectedIndex) return;
      this.hoverTimer = window.setTimeout(() => {
        this.hoverTimer = null;
        this.select(index);
      }, 70);
    }

    onFocusIn(event) {
      const index = this.rowIndex(event.target);
      if (index >= 0 && index !== this.selectedIndex) this.select(index);
    }

    clearHover() {
      if (this.hoverTimer !== null) window.clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }

    /* ---------- phone strip ---------- */

    prefersReducedMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    maxScroll() {
      return Math.max(0, this.strip.scrollWidth - this.strip.clientWidth);
    }

    cardLeft(index) {
      // offsetLeft is measured from the strip's padding box; the first card's offset is the snap origin.
      return Math.min(this.maxScroll(), Math.max(0, this.cards[index].offsetLeft - this.cards[0].offsetLeft));
    }

    currentStripIndex() {
      if (!this.strip) return 0;
      const left = this.strip.scrollLeft;
      if (left >= this.maxScroll() - 2) return this.cards.length - 1;
      let best = 0;
      let bestDistance = Infinity;
      this.cards.forEach((card, i) => {
        const distance = Math.abs(this.cardLeft(i) - left);
        if (distance < bestDistance) {
          best = i;
          bestDistance = distance;
        }
      });
      return best;
    }

    step(direction) {
      if (!this.strip) return;
      const from = this.targetIndex !== null ? this.targetIndex : this.currentStripIndex();
      const to = Math.max(0, Math.min(this.cards.length - 1, from + direction));
      if (to === from) return;
      this.prime();
      this.targetIndex = to;
      if (this.targetTimer !== null) window.clearTimeout(this.targetTimer);
      // Safety net if the scroll never reaches the target (e.g. the strip was resized mid-scroll).
      this.targetTimer = window.setTimeout(() => {
        this.targetTimer = null;
        this.targetIndex = null;
        this.onScroll();
      }, 1200);
      this.updateCount(to);
      this.strip.scrollTo({ left: this.cardLeft(to), behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    onStripTouch() {
      // A swipe takes over from a button scroll still in progress.
      this.prime();
      this.targetIndex = null;
    }

    onScroll() {
      if (this.scrollFrame !== null || !this.strip) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        if (this.targetIndex !== null) {
          if (Math.abs(this.strip.scrollLeft - this.cardLeft(this.targetIndex)) > 2) return;
          this.targetIndex = null;
        }
        this.updateCount(this.currentStripIndex());
      });
    }

    updateCount(index) {
      const total = this.cards.length;
      const changed = index !== this.stripIndex;
      this.stripIndex = index;
      if (this.countText) this.countText.textContent = `${index + 1} / ${total}`;
      if (this.countLabel && (changed || !this.countLabel.textContent)) this.countLabel.textContent = `Product ${index + 1} of ${total}`;
      if (this.prevButton) this.prevButton.setAttribute('aria-disabled', String(index <= 0));
      if (this.nextButton) this.nextButton.setAttribute('aria-disabled', String(index >= total - 1));
      // Drives the soft right-edge fade in CSS: off once the strip cannot scroll any further.
      if (this.strip) this.classList.toggle('is-strip-end', this.strip.scrollLeft >= this.maxScroll() - 2);
    }

    /* ---------- photo priming ---------- */

    // Let the other photos load only after the page itself has loaded, so above-the-fold images have the network.
    schedulePrime() {
      this.cancelPrime();
      if (!this.isConnected || this.classList.contains('is-primed')) return;
      const later = () => {
        this.primeLoadListener = null;
        if (!this.isConnected) return;
        if (window.requestIdleCallback && window.cancelIdleCallback) {
          this.primeIdleCallback = window.requestIdleCallback(() => {
            this.primeIdleCallback = null;
            this.prime();
          }, { timeout: 2000 });
        } else {
          this.primeTimeout = window.setTimeout(() => {
            this.primeTimeout = null;
            this.prime();
          }, 200);
        }
      };
      if (document.readyState === 'complete') later();
      else {
        this.primeLoadListener = later;
        window.addEventListener('load', later, { once: true });
      }
    }

    cancelPrime() {
      if (this.primeLoadListener) {
        window.removeEventListener('load', this.primeLoadListener);
        this.primeLoadListener = null;
      }
      if (this.primeIdleCallback !== null) {
        window.cancelIdleCallback(this.primeIdleCallback);
        this.primeIdleCallback = null;
      }
      if (this.primeTimeout !== null) {
        window.clearTimeout(this.primeTimeout);
        this.primeTimeout = null;
      }
    }

    prime() {
      this.cancelPrime();
      if (!this.isConnected || this.classList.contains('is-primed')) return;
      this.classList.add('is-primed');
      this.querySelectorAll('.dh-sig__card img[loading="lazy"]').forEach((img, i) => {
        if (i > 0) img.fetchPriority = 'low';
        img.loading = 'eager';
      });
    }
  }

  customElements.define('dhrohar-signature', DhroharSignature);
}
